/**
 * Página de Prompts de IA do Sistema MEDICARE
 * Permite visualizar, criar, editar e gerenciar prompts de IA
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  VStack,
  Badge,
  useDisclosure,
  useColorModeValue,
  Tooltip,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  FormErrorMessage,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Code,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiCopy,
  FiPlay,
  FiFilter,
  FiDownload,
  FiTag,
} from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useNotifications } from '../../contexts/ToastContext';
import { PromptsService } from '../../services/prompts';

interface Prompt {
  id: string;
  nome: string;
  categoria: string;
  prompt_texto: string;
  parametros: {
    descricao?: string;
    variaveis?: string[];
    temperatura?: number;
    maxTokens?: number;
  };
  ativo: boolean;
  versao: number;
  criado_por?: string;
  data_criacao: string;
  data_atualizacao: string;
  criador_nome?: string;
  // Campos derivados para compatibilidade
  descricao?: string;
  conteudo?: string;
  variaveis?: string[];
  temperatura?: number;
  maxTokens?: number;
  criadoEm?: string;
  atualizadoEm?: string;
  criadoPor?: string;
  usosRecentes?: number;
}

interface PromptFormData {
  nome: string;
  descricao: string;
  categoria: string;
  conteudo: string;
  variaveis: string;
  ativo: boolean;
  temperatura: number;
  maxTokens: number;
}

const PROMPT_CATEGORIES = [
  { value: 'diagnostico', label: 'Diagnóstico', color: 'blue' },
  { value: 'tratamento', label: 'Tratamento', color: 'green' },
  { value: 'anamnese', label: 'Anamnese', color: 'purple' },
  { value: 'prescricao', label: 'Prescrição', color: 'orange' },
  { value: 'relatorio', label: 'Relatório', color: 'teal' },
  { value: 'educacional', label: 'Educacional', color: 'pink' },
  { value: 'geral', label: 'Geral', color: 'gray' },
];

export const PromptsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onViewModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  
  const { showSuccess, showError } = useNotifications();
  const queryClient = useQueryClient();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const codeBg = useColorModeValue('gray.50', 'gray.700');

  // Form para criar/editar prompt
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<PromptFormData>({
    defaultValues: {
      ativo: true,
      temperatura: 0.7,
      maxTokens: 1000,
    },
  });

  // Query para buscar prompts
  const {
    data: promptsResponse,
    isLoading,
    error,
  } = useQuery('prompts', async () => {
    const response = await PromptsService.listar({
      page: 1,
      limit: 100, // Buscar todos os prompts por enquanto
      ativo: true // Buscar apenas prompts ativos
    });
    return response;
  });

  // Mapear dados da API para o formato esperado pelo frontend
  const prompts = (promptsResponse?.dados?.prompts || []).map((prompt: any) => ({
    ...prompt,
    // Campos de compatibilidade
    descricao: prompt.parametros?.descricao || '',
    conteudo: prompt.prompt_texto,
    variaveis: prompt.parametros?.variaveis || [],
    temperatura: prompt.parametros?.temperatura || 0.7,
    maxTokens: prompt.parametros?.maxTokens || 1500,
    criadoEm: prompt.data_criacao,
    atualizadoEm: prompt.data_atualizacao,
    criadoPor: prompt.criador_nome || 'Sistema',
    usosRecentes: 0, // Será implementado posteriormente
  }));

  // Mutations
  const createPromptMutation = useMutation(
    (data: any) => PromptsService.criar(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prompts');
        showSuccess('Prompt criado com sucesso!');
        onModalClose();
        reset();
      },
      onError: () => {
        showError('Erro ao criar prompt');
      },
    }
  );

  const updatePromptMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => PromptsService.atualizar(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prompts');
        showSuccess('Prompt atualizado com sucesso!');
        onModalClose();
        reset();
        setIsEditing(false);
      },
      onError: () => {
        showError('Erro ao atualizar prompt');
      },
    }
  );

  const deletePromptMutation = useMutation(
    (id: string) => PromptsService.deletar(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prompts');
        showSuccess('Prompt excluído com sucesso!');
        onDeleteClose();
      },
      onError: () => {
        showError('Erro ao excluir prompt');
      },
    }
  );

  // Filtrar prompts
  const filteredPrompts = prompts.filter((prompt) => {
    const matchesSearch = prompt.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         prompt.descricao.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || prompt.categoria === filterCategory;
    const matchesStatus = !filterStatus || 
                         (filterStatus === 'ativo' && prompt.ativo) ||
                         (filterStatus === 'inativo' && !prompt.ativo);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getCategoryInfo = (category: string) => {
    return PROMPT_CATEGORIES.find(cat => cat.value === category) || PROMPT_CATEGORIES[6];
  };

  const handleCreatePrompt = () => {
    setIsEditing(false);
    setSelectedPrompt(null);
    reset({
      ativo: true,
      temperatura: 0.7,
      maxTokens: 1000,
    });
    onModalOpen();
  };

  const handleEditPrompt = (prompt: Prompt) => {
    setIsEditing(true);
    setSelectedPrompt(prompt);
    reset({
      nome: prompt.nome,
      descricao: prompt.descricao,
      categoria: prompt.categoria,
      conteudo: prompt.conteudo,
      variaveis: (prompt.variaveis || []).join(', '),
      ativo: prompt.ativo,
      temperatura: prompt.temperatura,
      maxTokens: prompt.maxTokens,
    });
    onModalOpen();
  };

  const handleViewPrompt = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    onViewModalOpen();
  };

  const handleDeletePrompt = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    onDeleteOpen();
  };

  const onSubmit = (data: PromptFormData) => {
    const formattedData = {
      nome: data.nome,
      categoria: data.categoria,
      prompt_texto: data.conteudo, // Mapear conteudo para prompt_texto
      parametros: {
        descricao: data.descricao,
        variaveis: data.variaveis.split(',').map(v => v.trim()).filter(v => v),
        temperatura: data.temperatura,
        maxTokens: data.maxTokens,
      },
      ativo: data.ativo,
    };

    if (isEditing && selectedPrompt) {
      updatePromptMutation.mutate({ id: selectedPrompt.id, data: formattedData });
    } else {
      createPromptMutation.mutate(formattedData);
    }
  };

  const confirmDelete = () => {
    if (selectedPrompt) {
      deletePromptMutation.mutate(selectedPrompt.id);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copiado para a área de transferência!');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (isLoading) {
    return <LoadingSpinner message="Carregando prompts..." />;
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="red.500">Erro ao carregar prompts</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Cabeçalho */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">Prompts de IA</Heading>
          <HStack spacing={3}>
            <Button
              leftIcon={<FiDownload />}
              variant="outline"
              size="sm"
            >
              Exportar
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="medicare"
              size="sm"
              onClick={handleCreatePrompt}
            >
              Novo Prompt
            </Button>
          </HStack>
        </Flex>

        {/* Filtros */}
        <Card>
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <FiSearch color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar por nome ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              
              <Select
                placeholder="Todas as categorias"
                maxW="200px"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                {PROMPT_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </Select>
              
              <Select
                placeholder="Todos os status"
                maxW="200px"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </Select>
              
              <Button
                leftIcon={<FiFilter />}
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilterCategory('');
                  setFilterStatus('');
                }}
              >
                Limpar
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {/* Grid de prompts */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
          {filteredPrompts.map((prompt) => {
            const categoryInfo = getCategoryInfo(prompt.categoria);
            return (
              <Card key={prompt.id} h="fit-content">
                <CardHeader pb={3}>
                  <Flex justify="space-between" align="start">
                    <VStack align="start" spacing={2} flex={1}>
                      <Heading size="sm" noOfLines={2}>
                        {prompt.nome}
                      </Heading>
                      <HStack spacing={2}>
                        <Badge
                          colorScheme={categoryInfo.color}
                          variant="subtle"
                          size="sm"
                        >
                          {categoryInfo.label}
                        </Badge>
                        <Badge
                          colorScheme={prompt.ativo ? 'green' : 'red'}
                          variant="subtle"
                          size="sm"
                        >
                          {prompt.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </HStack>
                    </VStack>
                    <HStack spacing={1}>
                      <Tooltip label="Visualizar">
                        <IconButton
                          aria-label="Visualizar prompt"
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewPrompt(prompt)}
                        />
                      </Tooltip>
                      <Tooltip label="Editar">
                        <IconButton
                          aria-label="Editar prompt"
                          icon={<FiEdit2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleEditPrompt(prompt)}
                        />
                      </Tooltip>
                      <Tooltip label="Excluir">
                        <IconButton
                          aria-label="Excluir prompt"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeletePrompt(prompt)}
                        />
                      </Tooltip>
                    </HStack>
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="start" spacing={3}>
                    <Text fontSize="sm" color="gray.600" noOfLines={3}>
                      {prompt.descricao}
                    </Text>
                    
                    <Box w="full">
                      <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>
                        Variáveis:
                      </Text>
                      <HStack spacing={1} wrap="wrap">
                        {(prompt.variaveis || []).map((variavel: string, index: number) => (
                          <Code key={index} fontSize="xs" colorScheme="blue">
                            {variavel}
                          </Code>
                        ))}
                      </HStack>
                    </Box>
                    
                    <Flex justify="space-between" w="full" fontSize="xs" color="gray.500">
                      <Text>Usos: {prompt.usosRecentes}</Text>
                      <Text>Atualizado: {formatDate(prompt.atualizadoEm)}</Text>
                    </Flex>
                    
                    <HStack spacing={2} w="full">
                      <Button
                        size="sm"
                        leftIcon={<FiCopy />}
                        variant="outline"
                        flex={1}
                        onClick={() => copyToClipboard(prompt.conteudo)}
                      >
                        Copiar
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<FiPlay />}
                        colorScheme="medicare"
                        flex={1}
                      >
                        Testar
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </Grid>

        {filteredPrompts.length === 0 && (
          <Card>
            <CardBody>
              <Text textAlign="center" color="gray.500">
                Nenhum prompt encontrado
              </Text>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Modal de criar/editar prompt */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? 'Editar Prompt' : 'Novo Prompt'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>
              <VStack spacing={4}>
                <Grid templateColumns="1fr 1fr" gap={4} w="full">
                  <FormControl isInvalid={!!errors.nome}>
                    <FormLabel>Nome</FormLabel>
                    <Input
                      {...register('nome', {
                        required: 'Nome é obrigatório',
                      })}
                    />
                    <FormErrorMessage>
                      {errors.nome?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.categoria}>
                    <FormLabel>Categoria</FormLabel>
                    <Select
                      {...register('categoria', {
                        required: 'Categoria é obrigatória',
                      })}
                    >
                      <option value="">Selecione uma categoria</option>
                      {PROMPT_CATEGORIES.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {errors.categoria?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Grid>

                <FormControl isInvalid={!!errors.descricao}>
                  <FormLabel>Descrição</FormLabel>
                  <Textarea
                    {...register('descricao', {
                      required: 'Descrição é obrigatória',
                    })}
                    rows={3}
                  />
                  <FormErrorMessage>
                    {errors.descricao?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.conteudo}>
                  <FormLabel>Conteúdo do Prompt</FormLabel>
                  <Textarea
                    {...register('conteudo', {
                      required: 'Conteúdo é obrigatório',
                    })}
                    rows={6}
                    placeholder="Digite o prompt aqui. Use {{variavel}} para definir variáveis."
                  />
                  <FormErrorMessage>
                    {errors.conteudo?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Variáveis (separadas por vírgula)</FormLabel>
                  <Input
                    {...register('variaveis')}
                    placeholder="nome_paciente, idade, sintomas"
                  />
                </FormControl>

                <Grid templateColumns="1fr 1fr 1fr" gap={4} w="full">
                  <FormControl>
                    <FormLabel>Temperatura</FormLabel>
                    <NumberInput min={0} max={2} step={0.1}>
                      <NumberInputField
                        {...register('temperatura', {
                          valueAsNumber: true,
                        })}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Max Tokens</FormLabel>
                    <NumberInput min={100} max={4000} step={100}>
                      <NumberInputField
                        {...register('maxTokens', {
                          valueAsNumber: true,
                        })}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Switch
                      {...register('ativo')}
                      colorScheme="medicare"
                      size="lg"
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      {watch('ativo') ? 'Ativo' : 'Inativo'}
                    </Text>
                  </FormControl>
                </Grid>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onModalClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                colorScheme="medicare"
                isLoading={isSubmitting}
              >
                {isEditing ? 'Atualizar' : 'Criar'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Modal de visualização */}
      <Modal isOpen={isViewModalOpen} onClose={onViewModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Visualizar Prompt</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPrompt && (
              <VStack spacing={4} align="stretch">
                <Grid templateColumns="1fr 1fr" gap={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Nome
                    </Text>
                    <Text mt={1}>{selectedPrompt.nome}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Categoria
                    </Text>
                    <Badge
                      colorScheme={getCategoryInfo(selectedPrompt.categoria).color}
                      variant="subtle"
                      mt={1}
                    >
                      {getCategoryInfo(selectedPrompt.categoria).label}
                    </Badge>
                  </Box>
                </Grid>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Descrição
                  </Text>
                  <Text mt={1}>{selectedPrompt.descricao}</Text>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Conteúdo
                  </Text>
                  <Box
                    mt={1}
                    p={3}
                    bg={codeBg}
                    borderRadius="md"
                    fontSize="sm"
                    whiteSpace="pre-wrap"
                  >
                    {selectedPrompt.conteudo}
                  </Box>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Variáveis
                  </Text>
                  <HStack spacing={2} mt={1} wrap="wrap">
                    {(selectedPrompt.variaveis || []).map((variavel: string, index: number) => (
                      <Code key={index} colorScheme="blue">
                        {variavel}
                      </Code>
                    ))}
                  </HStack>
                </Box>

                <Grid templateColumns="1fr 1fr 1fr" gap={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Temperatura
                    </Text>
                    <Text mt={1}>{selectedPrompt.temperatura}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Max Tokens
                    </Text>
                    <Text mt={1}>{selectedPrompt.maxTokens}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Status
                    </Text>
                    <Badge
                      colorScheme={selectedPrompt.ativo ? 'green' : 'red'}
                      variant="subtle"
                      mt={1}
                    >
                      {selectedPrompt.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </Box>
                </Grid>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onViewModalClose}>
              Fechar
            </Button>
            <Button
              colorScheme="medicare"
              onClick={() => {
                onViewModalClose();
                if (selectedPrompt) {
                  handleEditPrompt(selectedPrompt);
                }
              }}
            >
              Editar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir Prompt
            </AlertDialogHeader>

            <AlertDialogBody>
              Tem certeza que deseja excluir o prompt{' '}
              <strong>{selectedPrompt?.nome}</strong>? Esta ação não pode ser desfeita.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={deletePromptMutation.isLoading}
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default PromptsPage;